import { useTranslation } from 'react-i18next';
import { CarrierQuoteStateEnum, ExpeditionDetailV4OutDTO, QuotesPreviewOutDTO } from '@api/ringil-api.v2.tsx';
import { useContext } from 'react';
import { ApiContext } from '@api/api.tsx';

export const useExpeditionApproveMailto = () => {
  const { t } = useTranslation();
  const { getQuotesDbPaged, getCompanyUserDetail } = useContext(ApiContext);

  const fetchQuotesForDistribution = (
    expeditionId: string,
    distributionId: string,
  ): Promise<QuotesPreviewOutDTO[]> => {
    return new Promise((resolve) => {
      getQuotesDbPaged(
        expeditionId,
        distributionId,
        { onlyOffers: true },
        { pageNumber: 0, pageSize: 1000 },
        (response) => {
          resolve(response?.data ?? []);
        },
      );
    });
  };

  const fetchUserEmail = (userId: string): Promise<string | null> => {
    return new Promise((resolve) => {
      getCompanyUserDetail(userId, (data) => resolve(data?.email ?? null), null, { onError: () => resolve(null) });
    });
  };

  const buildMailto = async (data: ExpeditionDetailV4OutDTO): Promise<string> => {
    const na = t('expedition.approve.mailto.notAvailable', 'N/A');

    const allQuotesArrays = await Promise.all(
      (data?.distributions ?? []).map(distr =>
        fetchQuotesForDistribution(data.id, distr.distributionId),
      ),
    );

    const offeredQuotes = allQuotesArrays
      .flat()
      .filter(q => q.carrierQuoteState === CarrierQuoteStateEnum.OFFERED_PRICE);

    const cheapestQuote = offeredQuotes[0] ?? null;

    const carrierName = cheapestQuote?.carrierName ?? na;
    const price = cheapestQuote?.priceDTO?.price != null ? String(cheapestQuote.priceDTO.price) : na;
    const currency = cheapestQuote?.priceDTO?.currency ?? '';

    const creatorEmails = [...new Set((data?.distributions ?? []).map(d => d.createdByEmail).filter(Boolean))];

    const responsiblePersonUserId = data?.internalInfo?.responsiblePerson?.userId;
    const responsiblePersonEmail = responsiblePersonUserId ? await fetchUserEmail(responsiblePersonUserId) : null;

    const allEmails = [...new Set([...creatorEmails, responsiblePersonEmail, data?.createdByEmail].filter(Boolean))];
    const recipients = allEmails.join(';');

    const orderNames = [
      ...new Set(
        (data?.locations ?? [])
          .flatMap(loc => loc.loadedOrders ?? [])
          .map(o => o.orderName)
          .filter(Boolean),
      ),
    ].join(' - ');

    // Replace '/' with unicode division slash (U+2215) which looks identical
    // but won't break mailto URL parsing in browsers/email clients
    const mailtoSafe = (str: string) => str.replace(/\//g, '\u2215');

    const subject = t('expedition.approve.mailto.subject', '{{applicationId}} {{orderNames}}', {
      applicationId: mailtoSafe(data.applicationId),
      orderNames: mailtoSafe(orderNames),
    });

    const body = [
      t('expedition.approve.mailto.approvalRequest', 'Please approve extra transport.'),
      t('expedition.approve.mailto.cheapestOffer', 'Cheapest offer') + `:`,
      `  ${carrierName} - ${price} ${currency}`,
    ].join('\r\n');

    return `mailto:${recipients}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return { buildMailto };
};
